/* eslint-disable react/state-in-constructor */
/* eslint-disable react/sort-comp */
/* eslint-disable react/destructuring-assignment */
import { Box } from '@mantine/core';
import type {
	EditorDictionary,
	EmbedDescriptor,
	ExtensionsNames,
	ToastType,
} from '@repo/secoda-editor';
import { baseDictionary } from '@repo/secoda-editor';
import removeCommentMark from '@repo/secoda-editor/commands/removeCommentMark';
import { BlockMenu } from '@repo/secoda-editor/components/toolbars/BlockMenu';
import type Extension from '@repo/secoda-editor/lib/Extension';
import { isNil, memoize } from 'lodash-es';
import { Selection } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';
import type { MutableRefObject } from 'react';
import React from 'react';
import { ThemeProvider as OutlineThemeProvider } from 'styled-components';
import { PARENT_DOC_ID } from '../../../Documentation/utils';
import { EmojiMenu } from './components/EmojiMenu';
import Flex from './components/Flex';
import type { SearchResult } from './components/LinkEditor';
import LinkToolbar from './components/LinkToolbar';
import SelectionToolbar from './components/SelectionToolbar';
// Marks
import Bold from '@repo/secoda-editor/marks/Bold';
import Comment, {
	COMMENT_PLACEHOLDER_ID,
} from '@repo/secoda-editor/marks/Comment';
import Highlight from '@repo/secoda-editor/marks/Highlight';
import Italic from '@repo/secoda-editor/marks/Italic';
import Link from '@repo/secoda-editor/marks/Link';
import type Mark from '@repo/secoda-editor/marks/Mark';
import TemplatePlaceholder from '@repo/secoda-editor/marks/Placeholder';
import Strikethrough from '@repo/secoda-editor/marks/Strikethrough';
import Underline from '@repo/secoda-editor/marks/Underline';
import Attachment from '@repo/secoda-editor/nodes/Attachment';
import Blockquote from '@repo/secoda-editor/nodes/Blockquote';
import BulletList from '@repo/secoda-editor/nodes/BulletList';
import CheckboxItem from '@repo/secoda-editor/nodes/CheckboxItem';
import CheckboxList from '@repo/secoda-editor/nodes/CheckboxList';
import CodeBlock from '@repo/secoda-editor/nodes/CodeBlock';
import CodeFence from '@repo/secoda-editor/nodes/CodeFence';
import Embed from '@repo/secoda-editor/nodes/Embed';
import Emoji from '@repo/secoda-editor/nodes/Emoji';
import HardBreak from '@repo/secoda-editor/nodes/HardBreak';
import Heading from '@repo/secoda-editor/nodes/Heading';
import HorizontalRule from '@repo/secoda-editor/nodes/HorizontalRule';
import Image from '@repo/secoda-editor/nodes/Image';
import ListItem from '@repo/secoda-editor/nodes/ListItem';
import Notice from '@repo/secoda-editor/nodes/Notice';
import OrderedList from '@repo/secoda-editor/nodes/OrderedList';
import ChartBlock from './nodes/ChartBlock';
import Page from './nodes/Page';
import QueryBlock from './nodes/QueryBlock';
// Nodes
import type Node from '@repo/secoda-editor/nodes/Node';
import Table from '@repo/secoda-editor/nodes/Table';
import TableCell from '@repo/secoda-editor/nodes/TableCell';
import TableHeadCell from '@repo/secoda-editor/nodes/TableHeadCell';
import TableRow from '@repo/secoda-editor/nodes/TableRow';
// Plugins
import BlockMenuTrigger from '@repo/secoda-editor/plugins/BlockMenuTrigger';
import EmojiTrigger from '@repo/secoda-editor/plugins/EmojiTrigger';
import Folding from '@repo/secoda-editor/plugins/Folding';
import History from '@repo/secoda-editor/plugins/History';
import Keys from '@repo/secoda-editor/plugins/Keys';
import MaxLength from '@repo/secoda-editor/plugins/MaxLength';
import PasteHandler from '@repo/secoda-editor/plugins/PasteHandler';
import Placeholder from '@repo/secoda-editor/plugins/Placeholder';
import SmartText from '@repo/secoda-editor/plugins/SmartText';
import TrailingNode from '@repo/secoda-editor/plugins/TrailingNode';
import ResourceLink from './nodes/ResourceLink';

// Styles
import { EXTENSIONS_DISABLED_BY_DEFAULT } from '@repo/secoda-editor/lib/Extension';
import DataDiff from '@repo/secoda-editor/marks/DataDiff';
import AISummary from '@repo/secoda-editor/nodes/AISummary';
import TableOfContents from '@repo/secoda-editor/nodes/TableOfContents';
import FindAndReplaceTrigger from '@repo/secoda-editor/plugins/FindAndReplaceTrigger';
import Hash from '@repo/secoda-editor/plugins/HashPlugin';
import ScrollToHeading from '@repo/secoda-editor/plugins/ScrollToHeading';
import SpellCheck from '@repo/secoda-editor/plugins/SpellCheck';
import StyledEditor from '@repo/secoda-editor/styles/editor';
import { light as lightOutlineTheme } from '@repo/secoda-editor/styles/theme';
import { Toolbar } from '@repo/secoda-editor/types';
import { SecodaEditor } from '../../SecodaEditor';
import { FindAndReplace } from './components/FindAndReplace';
import { PlaceholderToolbar } from './components/PlaceholderToolbar';
import MentionTrigger from './extensions/MentionTrigger';
import AiChart from './nodes/AiChart';

export type IProseMirrorEditorProps = {
	id?: string;
	dataTestId?: string;
	value?: string;
	defaultValue: string;
	emptyLinePlaceholder?: string;
	emptyDocPlaceholder?: string;
	extensions?: Extension[];
	disableTopGap?: boolean;
	/**
	 * disabledExtensions is used to remove extensions that are enabled by default
	 * enableExtensions is used to add extensions that are disabled by default
	 */
	disableExtensions?: ExtensionsNames[];
	enableExtensions?: ExtensionsNames[];
	disableInputExtensions?: ExtensionsNames[];
	autoFocus?: boolean;
	readOnly?: boolean;
	readOnlyWriteCheckboxes?: boolean;
	dictionary?: Partial<EditorDictionary>;
	dir?: string;
	template?: boolean;
	maxLength?: number;
	scrollTo?: string;
	// eslint-disable-next-line react/no-unused-prop-types
	handleDOMEvents?: {
		[name: string]: (view: EditorView, event: Event) => boolean;
	};
	uploadFile?: (file: File, isImage: boolean) => Promise<string>;
	onBlur?: () => void;
	onFocus?: () => void;
	// @ts-expect-error TS(7031): Binding element 'done' implicitly has an 'any' typ... Remove this comment to see the full error message
	onSave?: ({ done }) => void;
	onCancel?: () => void;
	onChange?: (value: () => string | undefined) => void;
	onUnmount?: () => void;
	onFileUploadStart?: () => void;
	onFileUploadStop?: () => void;
	onCreateLink?: (title: string) => Promise<string>;
	onSearchLink?: (term: string) => Promise<SearchResult[]>;
	onClickLink: (href: string, event: MouseEvent) => void;
	focusedCommentID?: string;
	onCreatePlaceholderComment?: (selectedText: string) => void;
	onClickComment?: (commentID: string) => void;
	onCreateView?: () => void;
	onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
	embeds: EmbedDescriptor[];
	onShowToast: (message: string, code: ToastType | string) => void;
	className?: string;
	style?: React.CSSProperties;
	onTrackEvent?: (
		eventName: string,
		properties?: Record<string, string>
	) => void;
	singleLineEditor?: boolean;
	showMentionMenuButton?: boolean;
	disableResourceLinking?: boolean;
	editorRef?: MutableRefObject<SecodaEditor | null>;
	canMentionSecodaAi?: boolean;
	onPaste?: (e: React.ClipboardEvent<HTMLDivElement>) => void;
};

type State = {
	isRTL: boolean;
	isEditorFocused: boolean;
};

export class RichMarkdownEditor extends React.PureComponent<
	IProseMirrorEditorProps,
	State
> {
	static defaultProps = {
		defaultValue: '',
		dir: 'auto',
		placeholder: 'Write something nice…',
		onFileUploadStart: () => {
			// No default behavior
		},
		onFileUploadStop: () => {
			// No default behavior
		},
		embeds: [],
		extensions: [],
	};

	state = {
		isRTL: false,
		isEditorFocused: false,
	};

	// @ts-expect-error TS(2564): Property 'isBlurred' has no initializer and is not... Remove this comment to see the full error message
	isBlurred: boolean;

	element?: HTMLElement | null;

	editor: SecodaEditor;

	constructor(props: IProseMirrorEditorProps) {
		super(props);

		const self = this;

		// Add extensions that are disabled by default but not enabled
		const { enableExtensions = [], disableExtensions = [] } = this.props;
		for (const extension of EXTENSIONS_DISABLED_BY_DEFAULT.filter(
			(extension) => !enableExtensions?.includes(extension)
		)) {
			if (!disableExtensions.includes(extension)) {
				disableExtensions.push(extension);
			}
		}

		this.editor = new SecodaEditor({
			...this.props,
			disableExtensions,
			extensions: this.getAllExtensions(),
			handleChange: this.handleChange,
			onFinishedTransactions: () => {
				self.calculateDir();
			},
			onStateChange: () => {
				// Because SecodaEditor and React are not linked we must tell React that
				// a render is needed whenever the SecodaEditor state changes.
				// this is temporary! SecodaEditor will be changed into a mobx store in the future
				self.forceUpdate();
				// this.setState({
				// 	openedToolbar: toolbar,
				// 	blockMenuSearch: search ?? '',
				// });
			},
		});
		if (this.props.editorRef) {
			this.props.editorRef.current = this.editor;
		}
	}

	componentDidMount() {
		this.init();

		this.calculateDir();

		if (this.props.scrollTo) {
			this.editor.scrollToAnchor(this.props.scrollTo!);
		} else if (!this.props.readOnly && this.props.autoFocus) {
			this.focusAtEnd();
		}
	}

	componentDidUpdate(prevProps: IProseMirrorEditorProps) {
		// Allow changes to the 'value' prop to update the editor from outside
		if (!isNil(this.props.value) && prevProps.value !== this.props.value) {
			const newState = this.editor.createState(this.props.value);
			this.editor.view!.updateState(newState);
		}

		// Pass readOnly changes through to underlying editor instance
		if (prevProps.readOnly !== this.props.readOnly) {
			this.editor.setReadOnly(this.props.readOnly);
		}

		if (this.props.scrollTo && this.props.scrollTo !== prevProps.scrollTo) {
			this.editor.scrollToAnchor(this.props.scrollTo);
		}

		// Focus at the end of the document if switching from readOnly and autoFocus
		// is set to true
		if (prevProps.readOnly && !this.props.readOnly && this.props.autoFocus) {
			this.focusAtEnd();
		}

		// Also handle autoFocus changing from false to true (e.g., when comment form becomes active)
		if (!prevProps.autoFocus && this.props.autoFocus && !this.props.readOnly) {
			this.focusAtEnd();
		}

		if (prevProps.dir !== this.props.dir) {
			this.calculateDir();
		}

		if (
			!this.isBlurred &&
			!this.state.isEditorFocused &&
			!this.editor.openedToolbar
		) {
			this.isBlurred = true;
			if (this.props.onBlur) {
				this.props.onBlur();
			}
		}

		if (
			this.isBlurred &&
			(this.state.isEditorFocused || this.editor.openedToolbar)
		) {
			this.isBlurred = false;
			if (this.props.onFocus) {
				this.props.onFocus();
			}
		}
	}

	componentWillUnmount(): void {
		removeCommentMark(
			this.editor.view!.state,
			this.editor.view!.dispatch,
			COMMENT_PLACEHOLDER_ID
		);

		if (this.props.onUnmount) {
			this.props.onUnmount();
		}
	}

	init() {
		this.editor.createView(
			this.element,
			this.props.value || this.props.defaultValue || ''
		);
	}

	getAllExtensions() {
		const dictionary = this.dictionary(this.props.dictionary);

		const allExtensions = [
			...[
				new Emoji(),
				new Bold(),
				new Italic(),
				new Underline(),
				new Link(),
				new Strikethrough(),
				new History(),
				new TrailingNode(),
				new Placeholder({
					placeholder: this.props.emptyDocPlaceholder,
				}),
				new MaxLength({
					maxLength: this.props.maxLength,
				}),
				new Image({
					dictionary,
					uploadFile: this.props.uploadFile,
					onFileUploadStart: this.props.onFileUploadStart,
					onFileUploadStop: this.props.onFileUploadStop,
					onShowToast: this.props.onShowToast,
				}),
				new HardBreak(),
				new CodeBlock({
					dictionary,
					onShowToast: this.props.onShowToast,
				}),
				new CodeFence({
					dictionary,
					onShowToast: this.props.onShowToast,
				}),
				new Blockquote(),
				new Embed({ embeds: this.props.embeds }),
				new Attachment({
					dictionary,
				}),
				new Notice({
					dictionary,
				}),
				new Heading({
					dictionary,
					onShowToast: this.props.onShowToast,
				}),
				new HorizontalRule(),
				new Highlight(),
				new DataDiff(),
				new TemplatePlaceholder(),
				new Page(),
				new QueryBlock({
					dictionary,
					onShowToast: this.props.onShowToast,
					isTemplate: !!this.props.template,
				}),
				new ChartBlock({
					dictionary,
					onShowToast: this.props.onShowToast,
				}),
				new AiChart(),
				new ResourceLink(),
				new AISummary(),
				new TableOfContents(),

				new CheckboxList(),
				new CheckboxItem(),
				new BulletList(),
				new OrderedList(),
				new ListItem(),

				// the order matters here - we want Table to be last so that grip-table events are captured last and not compromise the grip-row / grip-column events defined in TableCell and TableHeadCell
				new TableCell(),
				new TableHeadCell(),
				new TableRow(),
				new Table(),
				// -

				new Comment({
					onCreatePlaceholderComment: this.props.onCreatePlaceholderComment,
					onClickComment: this.props.onClickComment,
					reference: this,
				}),
				new Folding(),
				new SmartText(),
				new PasteHandler(),
				new SpellCheck(),
				new ScrollToHeading(),
				new Hash(),
				new Keys({
					onBlur: this.handleEditorBlur,
					onFocus: this.handleEditorFocus,
					onSave: this.handleSave,
					onSaveAndExit: this.handleSaveAndExit,
					onCancel: this.props.onCancel,
				}),
				new FindAndReplaceTrigger(),
				new BlockMenuTrigger({
					emptyLinePlaceholder: this.props.emptyLinePlaceholder,
					emptyDocPlaceholder: this.props.emptyDocPlaceholder,
				}),
				new MentionTrigger({
					showMentionMenuButton: this.props.showMentionMenuButton,
				}),
				new EmojiTrigger(),
			],
			...(this.props.extensions || []),
		] as (Node | Mark | Extension)[];

		return allExtensions;
	}

	calculateDir = () => {
		if (!this.element) {
			return;
		}

		const isRTL =
			this.props.dir === 'rtl' ||
			getComputedStyle(this.element).direction === 'rtl';

		if (this.state.isRTL !== isRTL) {
			this.setState({ isRTL });
		}
	};

	value = (): string =>
		this.editor.serializer.serialize(this.editor.view!.state.doc);

	handleChange = () => {
		if (!this.props.onChange) {
			return;
		}

		this.props.onChange(() => (this.editor.view ? this.value() : undefined));
	};

	handleSave = () => {
		const { onSave } = this.props;
		if (onSave) {
			onSave({ done: false });
		}
	};

	handleSaveAndExit = () => {
		const { onSave } = this.props;
		if (onSave) {
			onSave({ done: true });
		}
	};

	handleEditorBlur = () => {
		this.setState({ isEditorFocused: false });
	};

	handleEditorFocus = () => {
		this.setState({ isEditorFocused: true });
	};

	// 'public' methods
	focusAtStart = () => {
		const selection = Selection.atStart(this.editor.view!.state.doc);
		const transaction = this.editor.view!.state.tr.setSelection(selection);
		this.editor.view!.dispatch(transaction);
		this.editor.view!.focus();
	};

	focusAtEnd = () => {
		requestAnimationFrame(() => {
			if (!this.editor?.view) {
				return;
			}

			try {
				const selection = Selection.atEnd(this.editor.view.state.doc);
				const transaction = this.editor.view.state.tr.setSelection(selection);
				this.editor.view.dispatch(transaction);
				this.editor.view.focus();
			} catch {
				// Silently handle any focus errors
			}
		});
	};

	createLineAtStart = () => {
		const { state, dispatch } = this.editor.view!;
		dispatch(state.tr.insert(0, state.schema.nodes.paragraph.create({})));
		this.focusAtStart();
	};

	dictionary: (
		providedDictionary?: Partial<EditorDictionary>
	) => EditorDictionary = memoize(
		(providedDictionary?: Partial<EditorDictionary>) => ({
			...baseDictionary,
			...providedDictionary,
		})
	);

	render() {
		const {
			dir,
			readOnly,
			readOnlyWriteCheckboxes,
			style,
			className,
			onKeyDown,
		} = this.props;
		const { isRTL } = this.state;
		const dictionary = this.dictionary(this.props.dictionary);

		return (
			<Flex
				onKeyDown={onKeyDown}
				style={style}
				className={className}
				align="flex-start"
				justify="center"
				dir={dir}
				column
			>
				<OutlineThemeProvider theme={lightOutlineTheme}>
					{!readOnly && !this.props.disableTopGap && (
						<Box
							onClick={this.createLineAtStart}
							sx={{
								marginTop: 5,
								marginBottom: 10,
								height: 2,
								width: '100%',
								':before': {
									cursor: 'pointer',
									content: '""',
									display: 'block',
									height: 30,
									marginTop: -15,
									width: '100%',
								},
								':hover': {
									backgroundColor: 'rgba(0,0,0,0.05)',
								},
							}}
						/>
					)}
					<>
						<StyledEditor
							onPaste={this.props.onPaste}
							id={PARENT_DOC_ID}
							data-testid={this.props.dataTestId ?? 'rich-text-editor'}
							focusedCommentID={this.props.focusedCommentID}
							dir={dir}
							rtl={isRTL}
							readOnly={readOnly}
							readOnlyWriteCheckboxes={readOnlyWriteCheckboxes}
							ref={(ref) => (this.element = ref)}
							data-with-mention-menu-button={this.props.showMentionMenuButton}
							spellCheck={false}
							data-autofocus={this.props.autoFocus ? 'true' : undefined}
							singleLineEditor={this.props.singleLineEditor ? true : undefined}
						/>
						{this.editor.view && (
							<SelectionToolbar
								view={this.editor.view}
								dictionary={dictionary}
								commands={this.editor.commands}
								rtl={isRTL}
								isTemplate={!!this.props.template}
								editor={this.editor}
								onSearchLink={this.props.onSearchLink}
								onClickLink={this.props.onClickLink}
								scrollToAnchor={this.editor.scrollToAnchor}
								onCreateLink={this.props.onCreateLink}
								onShowToast={this.props.onShowToast}
								isRestrictedToComments={readOnly}
								commentsEnabled={!!this.props.onClickComment}
							/>
						)}
						{!readOnly && this.editor.view && (
							<>
								<LinkToolbar
									view={this.editor.view}
									dictionary={dictionary}
									isActive={this.editor.openedToolbar === Toolbar.Link}
									onCreateLink={this.props.onCreateLink}
									onSearchLink={this.props.onSearchLink}
									onClickLink={this.props.onClickLink}
									scrollToAnchor={this.editor.scrollToAnchor}
									onShowToast={this.props.onShowToast}
									onClose={() => this.editor.closeToolbars(Toolbar.Link)}
								/>
								<PlaceholderToolbar
									view={this.editor.view}
									dictionary={dictionary}
									isActive={this.editor.openedToolbar === Toolbar.Placeholder}
									onClose={() => this.editor.closeToolbars(Toolbar.Placeholder)}
								/>
								{this.editor.openedToolbar === Toolbar.Emoji && (
									<EmojiMenu
										editor={this.editor}
										view={this.editor.view}
										commands={this.editor.commands}
										dictionary={dictionary}
										rtl={isRTL}
										search={this.editor.blockMenuSearch ?? ''}
										onClose={() => this.editor.closeToolbars(Toolbar.Emoji)}
										onTrackEvent={this.props.onTrackEvent}
									/>
								)}
								{this.editor.openedToolbar === Toolbar.BlockMenu && (
									<BlockMenu editor={this.editor} />
								)}
								{this.editor.openedToolbar === Toolbar.FindAndReplace && (
									<FindAndReplace
										editor={this.editor}
										dictionary={dictionary}
										readOnly={!!readOnly}
										onClose={() =>
											this.editor.closeToolbars(Toolbar.FindAndReplace)
										}
									/>
								)}
							</>
						)}
						{Array.from(this.editor.renderers).map((view) => view.content)}
						{Object.values(this.editor.widgets).map((Widget, index) => (
							<Widget
								// eslint-disable-next-line react/no-array-index-key
								key={index}
							/>
						))}
					</>
				</OutlineThemeProvider>
			</Flex>
		);
	}
}
